import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, StickyNote, Trash2, X } from "lucide-react";
import { api, formatApiError } from "../api";
import { useAuth } from "../AuthContext";

const STATUSES = ["Nuevo", "Contactado", "Visita agendada", "En seguimiento", "Contrato enviado", "Convertido", "No interesado"];

const STATUS_COLORS = {
  Nuevo: "bg-[#7D5A44] text-white",
  Contactado: "bg-[#B2967D] text-white",
  "Visita agendada": "bg-[#7D5A44]/80 text-white",
  "En seguimiento": "bg-[#B2967D] text-white",
  "Contrato enviado": "bg-[#7D5A44]/80 text-white",
  Convertido: "bg-[#7D5A44] text-white",
  "No interesado": "bg-black/70 text-white",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function formatDay(d) {
  if (!d) return "—";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("es-MX", { dateStyle: "medium" });
  } catch {
    return d;
  }
}

export function LeadsAdmin() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [leads, setLeads] = useState([]);
  const [active, setActive] = useState(null); // lead for notes panel
  const [noteText, setNoteText] = useState("");

  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const changeStatus = async (lead, status) => {
    try {
      const { data } = await api.patch(`/admin/leads/${lead.id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? data : l)));
      toast.success("Estatus actualizado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      const { data } = await api.post(`/admin/leads/${active.id}/notes`, { text: noteText.trim() });
      setLeads((prev) => prev.map((l) => (l.id === active.id ? data : l)));
      setActive(data);
      setNoteText("");
      toast.success("Nota agregada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (lead) => {
    if (!window.confirm(`¿Eliminar el prospecto ${lead.nombre}?`)) return;
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      toast.success("Prospecto eliminado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/leads/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "prospectos.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("No se pudo exportar");
    }
  };

  return (
    <div data-testid="admin-leads">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-black">Prospectos</h1>
          <p className="mt-2 text-sm font-light text-black/50">
            Todos los registros recibidos desde el sitio (formulario y agendar visita).
          </p>
        </div>
        {isSuper && (
          <button
            onClick={exportCsv}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 bg-[#7D5A44] text-white px-5 py-3 text-xs font-bold uppercase tracking-wide hover:bg-black transition-colors"
          >
            <Download size={16} /> Exportar CSV
          </button>
        )}
      </div>

      <div className="mt-8 bg-white border border-[#B2967D]/25 overflow-x-auto">
        <table className="w-full min-w-[1200px]">
          <thead>
            <tr className="bg-[#F5F1EA] text-left text-[11px] uppercase tracking-wide text-[#B2967D]">
              <th className="px-4 py-3 font-bold">Nombre</th>
              <th className="px-4 py-3 font-bold">Contacto</th>
              <th className="px-4 py-3 font-bold">Habitación interés</th>
              <th className="px-4 py-3 font-bold">Fecha entrada</th>
              <th className="px-4 py-3 font-bold">Presupuesto</th>
              <th className="px-4 py-3 font-bold">Personas</th>
              <th className="px-4 py-3 font-bold">Origen</th>
              <th className="px-4 py-3 font-bold">Mensaje</th>
              <th className="px-4 py-3 font-bold">Fecha</th>
              <th className="px-4 py-3 font-bold">Estatus</th>
              <th className="px-4 py-3 font-bold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && (
              <tr>
                <td colSpan={11} className="px-4 py-10 text-center text-sm font-light text-black/40">
                  Aún no hay prospectos registrados.
                </td>
              </tr>
            )}
            {leads.map((lead, i) => (
              <tr key={lead.id} className="border-t border-[#B2967D]/20 align-top" data-testid={`lead-row-${i}`}>
                <td className="px-4 py-3 text-sm font-bold text-black">{lead.nombre}</td>
                <td className="px-4 py-3 text-sm font-light text-black/70">
                  <div>{lead.correo}</div>
                  <div className="text-black/50">{lead.telefono}</div>
                </td>
                <td className="px-4 py-3 text-xs font-light text-black/70" data-testid={`lead-room-${i}`}>{lead.habitacion_interes || "—"}</td>
                <td className="px-4 py-3 text-xs font-light text-black/70 whitespace-nowrap">{formatDay(lead.fecha_entrada)}</td>
                <td className="px-4 py-3 text-xs font-light text-black/70">{lead.presupuesto || "—"}</td>
                <td className="px-4 py-3 text-xs font-light text-black/70" data-testid={`lead-people-${i}`}>{lead.num_personas || "—"}</td>
                <td className="px-4 py-3 text-xs font-light text-black/60">{lead.source}</td>
                <td className="px-4 py-3 text-sm font-light text-black/60 max-w-[200px]">{lead.mensaje}</td>
                <td className="px-4 py-3 text-xs font-light text-black/50 whitespace-nowrap">{formatDate(lead.created_at)}</td>
                <td className="px-4 py-3">
                  <select
                    value={lead.status}
                    onChange={(e) => changeStatus(lead, e.target.value)}
                    data-testid={`lead-status-${i}`}
                    className={`text-xs font-bold px-2 py-1.5 border-0 focus:outline-none cursor-pointer ${STATUS_COLORS[lead.status] || "bg-[#B2967D] text-white"}`}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s} className="bg-white text-black">{s}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => setActive(lead)}
                    data-testid={`lead-notes-${i}`}
                    className="inline-flex items-center gap-1 border border-[#B2967D]/50 text-black/70 px-3 py-2 text-xs hover:border-[#7D5A44] hover:text-[#7D5A44] transition-colors"
                  >
                    <StickyNote size={14} /> Notas ({lead.notes?.length || 0})
                  </button>
                  {isSuper && (
                    <button
                      onClick={() => remove(lead)}
                      data-testid={`lead-delete-${i}`}
                      className="ml-2 inline-flex items-center border border-[#B2967D]/50 text-black/60 px-3 py-2 text-xs hover:border-red-500 hover:text-red-600 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Notes drawer */}
      {active && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="notes-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setActive(null)} />
          <div className="relative w-full max-w-md bg-white h-full p-8 overflow-y-auto">
            <button onClick={() => setActive(null)} className="absolute top-5 right-5 text-black/40 hover:text-black">
              <X size={22} />
            </button>
            <h3 className="text-xl font-bold text-black">{active.nombre}</h3>
            <p className="mt-1 text-sm font-light text-black/50">{active.correo} · {active.telefono}</p>
            <p className="mt-1 text-xs font-light text-[#B2967D]">{active.source} · {formatDate(active.created_at)}</p>

            <div className="mt-6 grid grid-cols-1 gap-px bg-[#B2967D]/25 border border-[#B2967D]/25" data-testid="lead-detail-fields">
              <div className="bg-white px-4 py-3 flex justify-between gap-4">
                <span className="text-xs font-bold uppercase tracking-wide text-[#B2967D]">Habitación de interés</span>
                <span className="text-sm font-light text-black/80 text-right">{active.habitacion_interes || "—"}</span>
              </div>
              <div className="bg-white px-4 py-3 flex justify-between gap-4">
                <span className="text-xs font-bold uppercase tracking-wide text-[#B2967D]">Fecha deseada de entrada</span>
                <span className="text-sm font-light text-black/80 text-right">{formatDay(active.fecha_entrada)}</span>
              </div>
              <div className="bg-white px-4 py-3 flex justify-between gap-4">
                <span className="text-xs font-bold uppercase tracking-wide text-[#B2967D]">Presupuesto mensual</span>
                <span className="text-sm font-light text-black/80 text-right">{active.presupuesto || "—"}</span>
              </div>
              <div className="bg-white px-4 py-3 flex justify-between gap-4">
                <span className="text-xs font-bold uppercase tracking-wide text-[#B2967D]">Número de personas</span>
                <span className="text-sm font-light text-black/80 text-right">{active.num_personas || "—"}</span>
              </div>
            </div>

            {active.mensaje && <p className="mt-4 text-sm font-light text-black/70 bg-[#F5F1EA] p-4">{active.mensaje}</p>}

            <h4 className="mt-8 text-xs font-bold uppercase tracking-wide text-[#B2967D]">Notas internas</h4>
            <div className="mt-4 space-y-3">
              {(active.notes || []).length === 0 && (
                <p className="text-sm font-light text-black/40">Sin notas todavía.</p>
              )}
              {(active.notes || []).map((n, idx) => (
                <div key={idx} className="border-l-2 border-[#7D5A44] pl-3 py-1" data-testid={`note-${idx}`}>
                  <p className="text-sm font-light text-black/80">{n.text}</p>
                  <p className="text-[10px] text-[#B2967D] mt-1">{n.author} · {formatDate(n.created_at)}</p>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Ej. Se contactó vía WhatsApp el 15 de junio."
                rows={3}
                data-testid="note-input"
                className="w-full border border-[#B2967D]/50 px-3 py-2 text-sm font-light focus:outline-none focus:border-[#7D5A44] resize-none"
              />
              <button
                onClick={addNote}
                data-testid="note-add-btn"
                className="mt-3 w-full bg-[#7D5A44] text-white px-5 py-3 text-xs font-bold uppercase tracking-wide hover:bg-black transition-colors"
              >
                Agregar nota
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
